import { useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router';
import { HFBox, hfAlpha, useHfTheme } from '@hf/designSystem/v1';
import { useAccount } from '@core/utils/auth';
import { CustomLayout } from '../components/CustomLayout';
import { OrderBlotter } from '../components/OrderBlotter';
import { OrderPreview } from '../components/OrderPreview';
import { PositionLimitsGrid } from '../components/PositionLimitsGrid';
import { PositionsGrid } from '../components/PositionsGrid';
import { RuleBrowser } from '../components/RuleBrowser';
import { TradeBlotter } from '../components/TradeBlotter';
import { useBlotterRefresh } from '../hooks/useBlotterRefresh';
import { useMonitorOrders } from '../hooks/useMonitorOrders';
import { omsEnvBadge } from './omsStatus';
import { OmsTabStatus } from './OmsTabStatus';
import { isOmsTabSelected } from './omsNav';
import { OMS_SIMULATED_DATES } from './omsSimulatedDates';
import { SimulatedTradeDate } from './SimulatedTradeDate';

const OMS_TABS = [
  { path: 'create', name: 'Create Order' },
  { path: 'pretrade', name: 'PreTrade Compliance' },
  { path: 'monitor', name: 'Order Monitor' },
  { path: 'post-trade', name: 'Post Trade Compliance' },
  { path: 'blotter', name: 'Trade Blotter' },
  { path: 'custom', name: 'Custom layout' },
] as const;

/** Matches admin / tearsheets page gutter / sticky sub-nav bleed. */
const PAGE_GUTTER = 3;

/** Tab bar matches Tearsheets: sticky header, underline on the current tab. */
export function OmsLayout() {
  const { pathname } = useLocation();
  const theme = useHfTheme();
  const tab = OMS_TABS.find((item) => isOmsTabSelected(pathname, item.path));
  const [tradeDate, setTradeDate] = useState<string>(OMS_SIMULATED_DATES[0]);
  const stage = typeof import.meta.env.VITE_HF_ENV === 'string' ? import.meta.env.VITE_HF_ENV : undefined;
  const canSimulateTradeDate = stage !== 'uat' && stage !== 'prod';
  const createOpen = tab?.path === 'create';
  const monitorOpen = tab?.path === 'monitor';
  const blotterOpen = tab?.path === 'blotter';
  const customOpen = tab?.path === 'custom';
  const pretradeOpen = tab?.path === 'pretrade';
  const postTradeOpen = tab?.path === 'post-trade';
  const liveOpen = monitorOpen || customOpen;
  const refresh = useBlotterRefresh(liveOpen);
  const { user } = useAccount();
  const simulatedDate = canSimulateTradeDate ? tradeDate : undefined;
  const monitor = useMonitorOrders(simulatedDate, refresh.tick, liveOpen);
  const [fillsAsOf, setFillsAsOf] = useState<string | null>(null);
  const [fillsFailures, setFillsFailures] = useState(0);
  const [previewAsOf, setPreviewAsOf] = useState<string | null>(null);
  const [previewFailures, setPreviewFailures] = useState(0);
  const status = blotterOpen
    ? { asOf: fillsAsOf, failures: fillsFailures }
    : createOpen
      ? { asOf: previewAsOf, failures: previewFailures }
      : { asOf: monitor.asOf, failures: monitor.failures };
  const badge = omsEnvBadge(stage, user?.datasource);

  return (
    <HFBox
      sx={{
        width: 'auto',
        minWidth: 0,
        maxWidth: 'none',
        mx: -PAGE_GUTTER,
        mt: -PAGE_GUTTER,
        color: 'text.primary',
        ...(createOpen || monitorOpen || blotterOpen || customOpen
          ? {
              // Below md the shell adds a 64px app bar and a 24px gap above the page.
              height: { xs: 'calc(100dvh - 88px)', md: '100dvh' },
              // Cancel HFPageContainer bottom padding so both cards stay inside the viewport.
              mb: { xs: -PAGE_GUTTER, md: -6 },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }
          : null),
      }}
    >
      <HFBox
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 32,
          flexShrink: 0,
          px: PAGE_GUTTER,
          py: 1.25,
          mb: 2,
          boxSizing: 'border-box',
          bgcolor: hfAlpha(theme.palette.background.paper, 0.96),
          backdropFilter: 'blur(10px)',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <HFBox sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
          <HFBox component="nav" aria-label="OMS sections" sx={{ display: 'inline-flex', alignItems: 'stretch', gap: 0.25 }}>
            {OMS_TABS.map((item) => {
              const selected = isOmsTabSelected(pathname, item.path);
              return (
                <RouterLink key={item.path} to={item.path} aria-current={selected ? 'page' : undefined} style={{ textDecoration: 'none' }}>
                  <HFBox
                    component="span"
                    sx={{
                      height: 36,
                      px: 1.75,
                      fontSize: 13,
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: 'transparent',
                      color: selected ? 'primary.main' : 'text.primary',
                      borderBottom: selected ? '2px solid' : '1px solid transparent',
                      borderColor: selected ? 'primary.main' : 'transparent',
                      borderRadius: 0,
                      boxSizing: 'border-box',
                      transition: 'color 0.15s ease, border-color 0.15s ease',
                      '&:hover': {
                        color: selected ? 'primary.main' : 'text.primary',
                        borderBottom: '2px solid',
                        borderColor: selected ? 'primary.main' : 'text.secondary',
                      },
                    }}
                  >
                    {item.name}
                  </HFBox>
                </RouterLink>
              );
            })}
          </HFBox>
          <HFBox sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <OmsTabStatus
              asOf={status.asOf}
              failures={status.failures}
              badge={badge}
              paused={liveOpen && refresh.held}
              refreshPaused={refresh.paused}
              onTogglePause={liveOpen ? refresh.togglePaused : undefined}
              freshness={!createOpen}
            />
          </HFBox>
        </HFBox>
      </HFBox>

      <HFBox
        sx={{
          width: '100%',
          maxWidth: theme.breakpoints.values.xl,
          mx: 'auto',
          boxSizing: 'border-box',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {createOpen ? (
          <HFBox sx={{ px: PAGE_GUTTER, pb: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <OrderPreview
              date={simulatedDate}
              onRead={(ok) => {
                if (ok) {
                  setPreviewAsOf(new Date().toISOString());
                  setPreviewFailures(0);
                } else setPreviewFailures((count) => count + 1);
              }}
            />
          </HFBox>
        ) : null}

        {monitorOpen ? (
          <HFBox sx={{ px: PAGE_GUTTER, pb: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <OrderBlotter date={simulatedDate} orders={monitor.orders} ordersFailed={monitor.failed} positions={monitor.positions} />
            <PositionsGrid date={simulatedDate} positions={monitor.positionList} failed={monitor.positionsFailed} />
          </HFBox>
        ) : null}

        {blotterOpen ? (
          <HFBox sx={{ px: PAGE_GUTTER, pb: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <TradeBlotter
              date={simulatedDate}
              onRead={(ok) => {
                if (ok) {
                  setFillsAsOf(new Date().toISOString());
                  setFillsFailures(0);
                } else setFillsFailures((count) => count + 1);
              }}
            />
          </HFBox>
        ) : null}

        {customOpen ? (
          <HFBox sx={{ px: PAGE_GUTTER, pb: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CustomLayout date={simulatedDate} monitor={monitor} />
          </HFBox>
        ) : null}

        {pretradeOpen ? (
          <HFBox sx={{ px: PAGE_GUTTER, pb: 4 }}>
            <RuleBrowser stage="pre" date={simulatedDate} />
          </HFBox>
        ) : null}

        {postTradeOpen ? (
          <HFBox sx={{ px: PAGE_GUTTER, pb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <RuleBrowser stage="post" date={simulatedDate} />
            <PositionLimitsGrid date={simulatedDate} />
          </HFBox>
        ) : null}
      </HFBox>
      <Outlet />
      {canSimulateTradeDate ? (
        <HFBox sx={{ position: 'fixed', right: blotterOpen ? { xs: 16, lg: 616 } : 16, bottom: 16, zIndex: 40, boxShadow: 3, borderRadius: 1 }}>
          <SimulatedTradeDate value={tradeDate} onChange={setTradeDate} />
        </HFBox>
      ) : null}
    </HFBox>
  );
}
